<script setup lang="ts">
import { UiInfoBox, UiInputField, UiText } from '@shopexperts/shared';
import type { PageSchema } from '@shopexperts/shared';
import PageMetafieldFormField from './PageMetafieldFormField.vue';

const props = defineProps<{
  schema: PageSchema;
  pathError?: string;
}>();

const model = defineModel<Record<string, any>>({ required: true });
const path = defineModel<string>('path', { required: true });
</script>

<template>
  <div class="flex h-full min-h-0 w-full flex-col overflow-y-auto px-6 py-5">
    <div class="mb-5 flex items-start justify-between gap-3">
      <div class="min-w-0">
        <UiText as="h2" variant="h4" text="Metafields" />
        <UiText
          as="p"
          variant="p-small"
          class="mt-1 text-se-gray-700"
          :text="`Schema: ${props.schema.label}`"
        />
      </div>
      <!-- One flex item, so the actions stay a tight group on the right. -->
      <div class="flex shrink-0 items-center gap-0.5">
        <slot name="actions" />
      </div>
    </div>

    <div class="mb-5 flex max-w-3xl flex-col gap-3 border-b border-se-gray-200 pb-5">
      <div class="flex flex-col gap-1">
        <label for="page-path" class="font-archivo se-font-p">Page path</label>
        <UiInputField
          v-model="path"
          name="page-path"
          placeholder="/services/shopify-development-experts"
          hint="Changing this updates the public URL for the selected page when saved."
          :invalid="Boolean(props.pathError)"
          :error="props.pathError"
        />
      </div>
      <slot name="path-notice" />
    </div>

    <UiInfoBox v-if="!props.schema.fields.length" variant="warning">
      <UiText as="p" variant="p-small" text="This schema has no fields yet. Edit the schema to add fields before editing page values." />
    </UiInfoBox>

    <div v-else class="flex max-w-3xl flex-col gap-5">
      <PageMetafieldFormField
        v-for="field in props.schema.fields"
        :key="field.key"
        v-model="model"
        :field="field"
      />
    </div>
  </div>
</template>
